import React from "react";

import {
  ICategory,
  IClass,
  IOriginRealization,
  IRealization,
  IRealizationCreateParams,
  IRealizationImage,
  IStudentGearBox,
  ITargetSelectedData,
  ITeacher,
} from "../../../state";
import { ReactComponent as ImageIcon } from "../../../assets/icons/image.svg";
import {
  getUniqueSimpleArray,
  isImageFile,
  mergeArraysByUniqueKey,
  objectArrayToKeyArray,
} from "../../../libs/Util";
import {
  SELECT_REALIZATION_BOX_FAILURE_MESSAGE,
  SELECT_REALIZATION_CLASS_FAILURE_MESSAGE,
  SELECT_REALIZATION_TEACHER_FAILURE_MESSAGE,
} from "../../../store/student";
import RealizationTextArea, {
  CONTENT_MAX_LENGTH,
} from "../../../atoms/RealizationTextArea";
import RealizationButton from "../../../atoms/RealizationButton";
import RealizationThumbnailImage from "../../../atoms/RealizationThumbnailImage";
import WordCounter from "../../../atoms/WordCounter";
import CategoryForm from "../../../molecules/CategoryForm";
import BoxToggle from "../../../molecules/BoxToggle";
import ImageDetailModal from "../../../molecules/ImageDetailModal";
import ModalConfirm from "../../../molecules/ModalConfirm";
import OriginRealization from "../../../molecules/OriginRealization";
import Toast, { TToastType } from "../../../molecules/Toast";
import TargetGroup, { ETargetSwitch } from "../../TargetGroup";
import TemplateModal from "../../TemplateModal";
import { useBoxToggle } from "../../../hooks/useBoxToggle";
import { useTargetGroupUpdate } from "../../../hooks/useTargetGroupUpdate";
import { MAX_IMAGE_SIZE_MB } from "../../../constants/upload";

import styles from "./RealizationConversionForm.module.scss";

export interface RealizationConversionFormProps {
  realization: IRealization;
  categories: ICategory[];
  teachers: ITeacher[];
  classes: IClass[];
  isOpen: boolean;
  calling?: boolean;
  pathname: string;
  success_message: string;
  gearboxes: IStudentGearBox[];
  isActionInsight?: boolean;
  close: () => void;
  createCategory: (category: ICategory) => void;
  createDerivedRealization(
    realization: IRealizationCreateParams,
    submit_teacher_ids: number[],
    share_class_ids: number[],
    gearbox_id: number | null,
    image?: IRealizationImage,
  ): void;
  executeRealization?: (realization_id: number) => void;
}

const RealizationConversionForm = (props: RealizationConversionFormProps) => {
  const { realization } = props;
  const submitted_teacher_ids = objectArrayToKeyArray(
    realization.submitted_teachers,
  );
  const sharedClassIDs = objectArrayToKeyArray(realization.shared_classes);
  const categoryIDs =
    realization.categories?.map(category => category.id) ?? [];

  const realizationImgInputRef = React.useRef<HTMLInputElement>(null);
  const defaultTargetSwitch = React.useMemo(() => {
    if (realization.gearbox?.id) return "BOX" as const;
    if (realization.shared_classes?.length) return ETargetSwitch.GROUP;
    if (realization.submitted_teachers?.length) return ETargetSwitch.TEACHER;
    return ETargetSwitch.ONLY_ME;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realization.id]);

  const [state, setState] = React.useState<
    {
      category_ids: number[];
      selected: boolean;
      shared: boolean;
      content: string;
      toast: {
        type: TToastType;
        message: string;
      } | null;
      image?: IRealizationImage;
      showCategoryForm: boolean;
      showNewCategoryForm: boolean;
      showSubmitForm: boolean;
      showShareForm: boolean;
      showImageDetail: boolean;
      showImageInvalid: boolean;
      showImageExceedsSize: boolean;
      isBoxToggleOn: boolean;
      showBoxDropdown: boolean;
    } & ITargetSelectedData
  >(() => {
    let targetSelectedData: ITargetSelectedData = {
      selected_box_id: null,
      selected_teacher_ids: [],
      selected_class_ids: [],
      targetSwitch: ETargetSwitch.ONLY_ME,
    };
    if (realization.gearbox?.id) {
      targetSelectedData = {
        ...targetSelectedData,
        selected_box_id: realization.gearbox.id,
      };
    } else if (realization.shared_classes?.length) {
      targetSelectedData = {
        ...targetSelectedData,
        selected_class_ids: sharedClassIDs,
        targetSwitch: ETargetSwitch.GROUP,
      };
    } else if (realization.submitted_teachers?.length) {
      targetSelectedData = {
        ...targetSelectedData,
        selected_teacher_ids: submitted_teacher_ids,
        targetSwitch: ETargetSwitch.TEACHER,
      };
    }
    return {
      category_ids: categoryIDs,
      selected: false,
      shared: realization.shared,
      content: "",
      toast: null,
      image: undefined,
      showCategoryForm: false,
      showNewCategoryForm: false,
      showSubmitForm: false,
      showShareForm: false,
      showImageInvalid: false,
      showImageExceedsSize: false,
      showImageDetail: false,
      isBoxToggleOn: !!realization.gearbox?.id,
      showBoxDropdown: false,
      ...targetSelectedData,
    };
  });

  const update = React.useCallback((args: { [key: string]: any }) => {
    setState(prevState => ({ ...prevState, ...args }));
  }, []);

  const {
    boxDropdownRef,
    toggleButtonRef,
    selectedNameRef,
    handleBoxToggle,
    handleBoxSelect,
    handleToggleDropdown,
  } = useBoxToggle(setState, state.showBoxDropdown, {
    defaultTargetSwitch,
    originalGearboxId: realization.gearbox?.id ?? null,
  });

  const { handleTargetGroupUpdate } = useTargetGroupUpdate(state, setState, {
    defaultTargetSwitch,
    realization,
  });

  const classes = realization.shared_classes?.length
    ? (mergeArraysByUniqueKey({
        arrays: [props.classes, realization.shared_classes],
      }) as IClass[])
    : props.classes;

  const filteredGearboxes = React.useMemo(() => {
    const gearboxes =
      props.realization.gearbox &&
      !props.gearboxes.some(box => box.id === props.realization.gearbox?.id)
        ? [...props.gearboxes, props.realization.gearbox]
        : props.gearboxes;
    return gearboxes.filter(
      gearbox =>
        !gearbox.is_archived || gearbox.id === props.realization.gearbox?.id,
    ) as IStudentGearBox[];
  }, [props.gearboxes, props.realization.gearbox]);

  const formatRealization = React.useMemo(() => {
    return {
      id: realization.id,
      content: realization.content,
      created_at: realization.created_at?.toString(),
      kind: realization.kind,
      executed_at: realization.executed_at?.toString() || null,
    } as IOriginRealization;
  }, [realization]);

  React.useEffect(() => {
    if (realization) {
      const { shared } = realization;
      update({
        categoryIDs,
        shared,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realization]);

  const selectedBox = state.selected_box_id
    ? props.gearboxes.find(box => box.id === state.selected_box_id)
    : undefined;

  const onSubmit = () => {
    if (state.isBoxToggleOn && !state.selected_box_id) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_BOX_FAILURE_MESSAGE,
        },
      });
      return;
    }
    if (
      state.targetSwitch === ETargetSwitch.GROUP &&
      !state.selected_class_ids.length
    ) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_CLASS_FAILURE_MESSAGE,
        },
      });
      return;
    }
    if (
      state.targetSwitch === ETargetSwitch.TEACHER &&
      !state.selected_teacher_ids.length
    ) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_TEACHER_FAILURE_MESSAGE,
        },
      });
      return;
    }

    props.createDerivedRealization(
      {
        category_ids: state.category_ids,
        content: state.content,
        kind: props.isActionInsight ? "roots" : "will",
        submitted: state.selected_teacher_ids.length > 0,
        shared: state.selected_class_ids.length > 0,
        origin_realization_id: props.isActionInsight ? null : realization.id,
        reflection_realization_reference_id: props.isActionInsight
          ? realization.id
          : null,
      },
      !!selectedBox
        ? selectedBox.id === realization.gearbox?.id
          ? (getUniqueSimpleArray([
              ...submitted_teacher_ids,
              ...selectedBox.assigned_teachers.map(teacher => teacher.id),
            ]) as number[])
          : selectedBox.assigned_teachers.map(teacher => teacher.id)
        : state.selected_teacher_ids,
      !!selectedBox
        ? selectedBox.id === realization.gearbox?.id
          ? (getUniqueSimpleArray([
              ...sharedClassIDs,
              ...realization.gearbox.klasses.map(cls => cls.id),
            ]) as number[])
          : selectedBox.klasses.map(cls => cls.id)
        : state.selected_class_ids,
      state.selected_box_id ?? null,
      state.image,
    );
    props.close();
  };

  const removeImg = React.useCallback(() => {
    realizationImgInputRef.current &&
      (realizationImgInputRef.current.value = "");
    update({ image: undefined });
  }, [update]);

  const uploadImg = React.useCallback(
    e => {
      e.persist();
      const file = e.target.files[0];
      if (!file) {
        return;
      }
      if (!isImageFile(file)) {
        removeImg();
        update({ showImageInvalid: true });
        return;
      }
      const fileSizeByMB = file.size / 1024 / 1024;
      if (fileSizeByMB > MAX_IMAGE_SIZE_MB) {
        removeImg();
        update({ showImageExceedsSize: true });
        return;
      }
      update({
        image: {
          name: file.name,
          url: URL.createObjectURL(file),
          blob: file,
        },
      });
    },
    [removeImg, update],
  );

  return (
    <TemplateModal
      isOpen={props.isOpen}
      onClose={props.close}
      headerTitle={
        props.isActionInsight
          ? "行動から生まれた気づきをストックしてみよう。"
          : "WILL化"
      }
    >
      <div className={styles.wrapper}>
        <ModalConfirm
          isOpen={state.showImageInvalid}
          message={`無効なファイル形式です。\njpg/jpeg/png のみアップロード可能です。`}
          buttons={[
            {
              title: "キャンセル",
              type: "None",
              action: () => update({ showImageInvalid: false }),
            },
            {
              title: "OK",
              type: "Success",
              action: () => update({ showImageInvalid: false }),
            },
          ]}
        />
        <ModalConfirm
          isOpen={state.showImageExceedsSize}
          message={`アップロードに失敗しました。\nファイルサイズを${MAX_IMAGE_SIZE_MB}MB以下にしてください。`}
          buttons={[
            {
              title: "OK",
              type: "None",
              action: () => update({ showImageExceedsSize: false }),
            },
          ]}
        />
        {state.image && (
          <ImageDetailModal
            show={state.showImageDetail}
            imageUrl={state.image.url}
            imageName={state.image.name}
            onClose={() => update({ showImageDetail: false })}
          />
        )}
        <Toast
          type={state.toast?.type}
          showToast={!!state.toast}
          onClose={() => update({ toast: null })}
          message={state.toast?.message ?? ""}
        />
        <div className={styles.container}>
          {!props.isActionInsight && (
            <BoxToggle
              isBoxToggleOn={state.isBoxToggleOn}
              showBoxDropdown={state.showBoxDropdown}
              selectedBox={selectedBox}
              gearBoxes={filteredGearboxes}
              selected_box_id={state.selected_box_id}
              toggleButtonRef={toggleButtonRef}
              selectedNameRef={selectedNameRef}
              boxDropdownRef={boxDropdownRef}
              onToggle={handleBoxToggle}
              onToggleDropdown={handleToggleDropdown}
              onSelectBox={handleBoxSelect}
            />
          )}
          <div className={styles.inputArea}>
            <div className={styles.textAreaWrapper}>
              <RealizationTextArea
                content={state.content}
                onChange={value => setState({ ...state, content: value })}
                placeholder={
                  props.isActionInsight
                    ? "行動してみた結果、どんな気づき（ROOTS）がありましたか？"
                    : undefined
                }
              />
            </div>
            <div className={styles.imgInputWrapper}>
              {state.image?.url && (
                <div className={styles.realizationImg}>
                  <RealizationThumbnailImage
                    alt="Upload Image"
                    src={state.image.url}
                    onClick={e => {
                      e.stopPropagation();
                      update({ showImageDetail: true });
                    }}
                    isEdit={true}
                  />
                  <button onClick={removeImg}>✕</button>
                </div>
              )}
              <div className={styles.rightControlArea}>
                <WordCounter
                  maxLength={CONTENT_MAX_LENGTH}
                  targetLength={state.content.length}
                />
                {!state.image && (
                  <>
                    <input
                      ref={realizationImgInputRef}
                      accept=".png,.jpg,.jpeg"
                      type="file"
                      onChange={uploadImg}
                      className={styles.hiddenInput}
                    />
                    <ImageIcon
                      onClick={() => realizationImgInputRef.current?.click()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.originWrapper}>
            <OriginRealization realization={formatRealization} />
          </div>
          {props.isActionInsight && realization.gearbox && (
            <div className={styles.originWrapper}>
              {realization.origin_realization && (
                <OriginRealization
                  realization={realization.origin_realization}
                  showAuthor={true}
                  isDetail={true}
                />
              )}
            </div>
          )}
          {!props.isActionInsight && (
            <TargetGroup
              realization={realization}
              teachers={props.teachers}
              classes={classes}
              showSubmitForm={state.showSubmitForm}
              showShareForm={state.showShareForm}
              targetSwitch={state.isBoxToggleOn ? null : state.targetSwitch}
              selected_teacher_ids={state.selected_teacher_ids}
              selected_class_ids={state.selected_class_ids}
              update={handleTargetGroupUpdate}
              excludeRef={boxDropdownRef}
              disabled={state.isBoxToggleOn && !!state.selected_box_id}
            />
          )}
          <CategoryForm
            pathname={props.pathname}
            success_message={props.success_message}
            calling={props.calling}
            isOpen={state.showCategoryForm}
            categories={props.categories}
            selectedCategoryIDs={state.category_ids}
            update={update}
            createCategory={props.createCategory}
          />
        </div>
        <div className={styles.wrapperButton}>
          {props.isActionInsight ? (
            <RealizationButton
              kind={"roots"}
              isDisabled={state.content === "" || props.calling}
              onClick={() => {
                onSubmit();
                if (props.executeRealization) {
                  props.executeRealization(realization.id);
                }
              }}
            />
          ) : (
            <RealizationButton
              kind={"will"}
              isDisabled={state.content === "" || props.calling}
              onClick={onSubmit}
            />
          )}
        </div>
      </div>
    </TemplateModal>
  );
};

export default RealizationConversionForm;
